import {
  type BillingContact,
  createBillingContact,
  deleteBillingContact,
  deserializeBillingContact,
  fetchBillingContacts,
  fetchBillingDetail,
} from "@app/billing";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useCache,
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { useEffect, useMemo, useState } from "react";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  FormGroup,
  Group,
  Input,
  Loading,
  TitleBar,
} from "../shared";

export const BillingContactsPage = () => {
  const org = useSelector(selectOrganizationSelected);
  const billingDetailId = org.billingDetailId || org.id;
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [lastSubmittedContact, setLastSubmittedContact] = useState<{
    name: string;
    email: string;
  } | null>(null);
  const [optimisticContacts, setOptimisticContacts] = useState<
    BillingContact[]
  >([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [lastDeleteAttemptId, setLastDeleteAttemptId] = useState<string | null>(
    null,
  );

  useQuery(fetchBillingDetail({ id: billingDetailId }));
  const contactsCache = useCache(fetchBillingContacts({ id: billingDetailId }));

  const createLoader = useLoader(createBillingContact);
  const deleteLoader = useLoader(deleteBillingContact);

  useLoaderSuccess(createLoader, () => {
    if (lastSubmittedContact) {
      setOptimisticContacts((prev) => [
        ...prev,
        {
          id: `optimistic-${Date.now()}`,
          name: lastSubmittedContact.name,
          email: lastSubmittedContact.email,
        },
      ]);
      setLastSubmittedContact(null);
    }
    setName("");
    setEmail("");
    contactsCache.trigger();
  });
  useLoaderSuccess(deleteLoader, () => {
    setLastDeleteAttemptId(null);
    contactsCache.trigger();
  });
  useEffect(() => {
    if (deleteLoader.isError && lastDeleteAttemptId) {
      setPendingDeleteIds((prev) =>
        prev.filter((id) => id !== lastDeleteAttemptId),
      );
      setLastDeleteAttemptId(null);
    }
  }, [deleteLoader.isError, lastDeleteAttemptId]);

  const contactsFromCache = useMemo(() => {
    return (contactsCache.data?._embedded?.billing_contacts || []).map(
      deserializeBillingContact,
    );
  }, [contactsCache.data]);

  useEffect(() => {
    if (contactsFromCache.length === 0) {
      return;
    }
    setOptimisticContacts((prev) => {
      const next = prev.filter(
        (optimistic) =>
          !contactsFromCache.some(
            (contact) =>
              contact.name.toLowerCase() === optimistic.name.toLowerCase() &&
              contact.email.toLowerCase() === optimistic.email.toLowerCase(),
          ),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [contactsFromCache]);

  const contacts = useMemo(() => {
    const merged = [...contactsFromCache, ...optimisticContacts];
    const seen = new Set<string>();
    return merged
      .filter((contact) => {
        const key = `${contact.email.toLowerCase()}::${contact.name.toLowerCase()}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .filter((contact) => !pendingDeleteIds.includes(contact.id));
  }, [contactsFromCache, optimisticContacts, pendingDeleteIds]);

  useEffect(() => {
    setPendingDeleteIds((prev) => {
      const next = prev.filter((id) =>
        contactsFromCache.some((contact) => contact.id === id),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [contactsFromCache]);
  const isLoading = !contactsCache.data && !contactsCache.lastSuccess;

  const onAddContact = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Please fill in all required fields.");
      return;
    }
    const submittedContact = {
      name: name.trim(),
      email: email.trim(),
    };
    setLastSubmittedContact(submittedContact);
    dispatch(
      createBillingContact({
        id: billingDetailId,
        name: submittedContact.name,
        email: submittedContact.email,
      }),
    );
  };

  const onDeleteContact = (contact: { id: string; name: string }) => {
    if (deleteLoader.isLoading) {
      return;
    }
    if (contact.id.startsWith("optimistic-")) {
      return;
    }
    const confirmed = window.confirm(
      `Delete billing contact "${contact.name}"? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }
    setLastDeleteAttemptId(contact.id);
    setPendingDeleteIds((prev) => [...prev, contact.id]);
    dispatch(deleteBillingContact({ id: contact.id }));
  };

  return (
    <Group>
      <TitleBar description="Manage who receives billing invoices and notices">
        Billing Contacts
      </TitleBar>

      <BannerMessages {...createLoader} />
      <BannerMessages {...deleteLoader} />
      {error ? <Banner variant="error">{error}</Banner> : null}

      <Box>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loading />
          </div>
        ) : contacts.length === 0 ? (
          <p className="text-black-500 py-2">You have no Billing Contacts.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black-100">
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Email Address</th>
                <th className="text-left py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact: BillingContact) => (
                <tr key={contact.id} className="border-b border-black-100">
                  <td className="py-2">{contact.name}</td>
                  <td className="py-2">{contact.email}</td>
                  <td className="py-2">
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => onDeleteContact(contact)}
                      isLoading={
                        deleteLoader.isLoading &&
                        lastDeleteAttemptId === contact.id
                      }
                      disabled={
                        contact.id.startsWith("optimistic-") ||
                        deleteLoader.isLoading
                      }
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>

      <Box>
        <form className="space-y-4" onSubmit={onAddContact}>
          <h3 className="font-semibold text-black">Add Billing Contact</h3>
          <FormGroup label="Name" htmlFor="billing-contact-name">
            <Input
              id="billing-contact-name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
            />
          </FormGroup>
          <FormGroup label="Email Address" htmlFor="billing-contact-email">
            <Input
              id="billing-contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
            />
          </FormGroup>
          <Button type="submit" isLoading={createLoader.isLoading}>
            Save
          </Button>
        </form>
      </Box>
    </Group>
  );
};
